import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { X, Plus } from "lucide-react";

const PlanModal = ({ plan, onClose, onSave, currentPlans = [] }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    billingCycle: "monthly",
    suggestedOrgSize: "",
    features: [],
    isPopular: false,
    isActive: true,
  });
  const [newFeature, setNewFeature] = useState("");

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name || "",
        description: plan.description || "",
        price: plan.price?.toString() || "",
        billingCycle: plan.billingCycle || "monthly",
        suggestedOrgSize: plan.suggestedOrgSize || "",
        features: plan.features || [],
        isPopular: plan.isPopular || false,
        isActive: plan.isActive !== undefined ? plan.isActive : true,
      });
    }
  }, [plan]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, newFeature.trim()],
      }));
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.price || !formData.billingCycle) {
      alert("Please fill in all required fields");
      return;
    }

    // Convert string values to numbers
    // All plans have unlimited users (userLimit always -1)
    const submitData = {
      ...formData,
      price: parseFloat(formData.price),
      suggestedOrgSize: formData.suggestedOrgSize.trim() || undefined,
    };

    onSave(submitData);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {plan ? "Edit Subscription Plan" : "Create Subscription Plan"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Plan Name */}
          <div>
            <Label
              htmlFor="name"
              className="text-sm font-semibold text-gray-900"
            >
              Plan Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g., Basic, Premium, Enterprise"
              className="mt-2 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label
              htmlFor="description"
              className="text-sm font-semibold text-gray-900"
            >
              Description
            </Label>
            <Input
              id="description"
              type="text"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Brief description of the plan"
              className="mt-2 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* Price and Billing Cycle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="price"
                className="text-sm font-semibold text-gray-900"
              >
                Price ($) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => handleChange("price", e.target.value)}
                placeholder="29.99"
                className="mt-2 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                required
              />
            </div>

            <div>
              <Label
                htmlFor="billingCycle"
                className="text-sm font-semibold text-gray-900"
              >
                Billing Cycle <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.billingCycle}
                onValueChange={(value) => handleChange("billingCycle", value)}
              >
                <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Select billing cycle" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Suggested Organization Size */}
          <div>
            <Label
              htmlFor="suggestedOrgSize"
              className="text-sm font-semibold text-gray-900"
            >
              Suggested Organization Size
            </Label>
            <Select
              value={formData.suggestedOrgSize || undefined}
              onValueChange={(value) => handleChange("suggestedOrgSize", value === "none" ? undefined : value)}
            >
              <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                <SelectValue placeholder="Select organization size (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">None (no suggestion)</SelectItem>
                <SelectItem value="1-500">1-500</SelectItem>
                <SelectItem value="501-5000">501-5000</SelectItem>
                <SelectItem value="5000+">5000+</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-600 mt-2">
              Display-only suggestion shown on the website. All plans have unlimited users.
            </p>
          </div>

          {/* Features */}
          <div>
            <Label className="text-sm font-semibold text-gray-900">
              Features
            </Label>
            <div className="mt-2 space-y-2">
              {formData.features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-gray-100 p-3 rounded-md border border-gray-200"
                >
                  <span className="flex-1 text-sm text-gray-900">
                    {feature}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveFeature(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Add a feature"
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddFeature();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleAddFeature}
                variant="outline"
                className="shrink-0 border-gray-300 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Switches */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
              <div>
                <Label className="text-sm font-semibold text-gray-900">
                  Popular Plan
                </Label>
                <p className="text-xs text-gray-600 mt-1">
                  Mark this plan as popular/recommended
                </p>
              </div>
              <Switch
                checked={formData.isPopular}
                onCheckedChange={(checked) =>
                  handleChange("isPopular", checked)
                }
              />
            </div>

            <div className="flex flex-col p-4 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-sm font-semibold text-gray-900">
                    Active
                  </Label>
                  <p className="text-xs text-gray-600 mt-1">
                    Make this plan available for selection
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    handleChange("isActive", checked)
                  }
                />
              </div>
              {currentPlans.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  ℹ️ Note: You must maintain exactly 3 active plans. Currently{" "}
                  {currentPlans.filter((p) => p.isActive).length} plans are
                  active.
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="!bg-green-500 !hover:bg-green-600 !text-white font-medium px-6 py-2"
            >
              {plan ? "Update Plan" : "Create Plan"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PlanModal;
